/**
 * Shared procedural one-shots (no external assets required).
 * Swap for AudioBufferSourceNode + fetched files when URLs are provided.
 */

/**
 * @param {AudioContext} ctx
 * @param {number} durationSeconds
 * @returns {AudioBuffer}
 */
export function createWhiteNoiseBuffer(ctx, durationSeconds = 0.12) {
  const length = Math.max(1, Math.floor(ctx.sampleRate * durationSeconds));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

/**
 * @param {AudioContext} ctx
 * @param {GainNode} destination
 * @param {{
 *   frequency?: number;
 *   type?: OscillatorType;
 *   duration?: number;
 *   gain?: number;
 *   attack?: number;
 *   decay?: number;
 *   detune?: number;
 * }} params
 */
export function playTone(ctx, destination, params = {}) {
  const {
    frequency = 440,
    type = 'sine',
    duration = 0.08,
    gain = 0.08,
    attack = 0.004,
    decay = 0.06,
    detune = 0,
  } = params;

  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.value = frequency;
  osc.detune.value = detune;

  const t0 = ctx.currentTime;
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0001), t0 + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);

  osc.connect(env);
  env.connect(destination);
  osc.start(t0);
  osc.stop(t0 + duration);
}

/**
 * @param {AudioContext} ctx
 * @param {GainNode} destination
 * @param {{
 *   duration?: number;
 *   gain?: number;
 *   filterFreq?: number;
 *   filterQ?: number;
 * }} params
 */
export function playNoiseBurst(ctx, destination, params = {}) {
  const {
    duration = 0.05,
    gain = 0.12,
    filterFreq = 800,
    filterQ = 0.7,
  } = params;

  const buffer = createWhiteNoiseBuffer(ctx, duration);
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = filterFreq;
  filter.Q.value = filterQ;

  const env = ctx.createGain();
  const t0 = ctx.currentTime;
  env.gain.setValueAtTime(gain, t0);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

  source.connect(filter);
  filter.connect(env);
  env.connect(destination);
  source.start(t0);
  source.stop(t0 + duration + 0.01);
}

/**
 * Optional future asset hook — fetch decode when path is set.
 * @param {AudioContext} ctx
 * @param {string | null | undefined} url
 * @returns {Promise<AudioBuffer | null>}
 */
export async function loadAudioBuffer(ctx, url) {
  if (!url) return null;
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  return ctx.decodeAudioData(arrayBuffer);
}
